import axios from 'axios';
import { getRwgToken } from './rwgToken';

// API configuration - matching the logic from App.js
const API_BASE_URL = (
  process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL
    : window.location.hostname === 'localhost' ? 'http://localhost:8000'
      : window.location.hostname === 'scheduler.clondon.vm.cinch.io' ? 'https://be.clondon.vm.cinch.io'
        : window.location.hostname === 'scheduler.dev.aws.cinch.io' ? 'https://engine-dev.cinch.io'
          : 'https://engine.cinch.io'
);


// Create axios instance with shared configuration
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const getLocations = async (calendarId) => {
  try {
    const response = await api.get(`/calendar/calendars/${calendarId}/locations`);
    return response.data;
  } catch (error) {
    console.error('Error fetching locations:', error);
    throw error;
  }
};

export const createSession = async (calendarId) => {
  try {
    // CIN-6443: attach the Reserve with Google token only when one is stored,
    // so non-Google visitors' request bodies stay byte-identical ({}).
    const rwgToken = getRwgToken(calendarId);
    const body = rwgToken ? { rwg_token: rwgToken } : {};
    const response = await api.post(`/calendar/calendars/${calendarId}/sessions`, body);
    return response.data;
  } catch (error) {
    console.error('Error creating session:', error);
    throw error;
  }
};

export const updateSession = async (calendarId, sessionId, sessionData) => {
  try {
    // CIN-6443: include rwg_token only when a valid token is stored; otherwise
    // the body is unchanged (preserves existing payload assertions/behavior).
    const rwgToken = getRwgToken(calendarId);
    const body = rwgToken ? { ...sessionData, rwg_token: rwgToken } : sessionData;
    const response = await api.patch(
      `/calendar/calendars/${calendarId}/sessions/${sessionId}`,
      body
    );
    return response.data;
  } catch (error) {
    console.error('Error updating session:', error);
    throw error;
  }
};

export const getSession = async (calendarId, sessionId) => {
  try {
    const response = await api.get(
      `/calendar/calendars/${calendarId}/sessions/${sessionId}`
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching session:', error);
    throw error;
  }
};

export const getInitialAvailability = async (calendarId, sessionId, locationId, serviceIds) => {
  try {
    const params = new URLSearchParams({
      session: sessionId,
      location: locationId,
    });

    // Add each service as a separate query parameter
    if (Array.isArray(serviceIds)) {
      serviceIds.forEach(serviceId => {
        params.append('services', serviceId);
      });
    } else {
      params.set('services', serviceIds);
    }

    // No start_date parameter for initial availability - returns all available slots
    const response = await api.get(`/calendar/calendars/${calendarId}/availability?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching initial availability:', error);
    throw error;
  }
};

export const getAvailability = async (calendarId, sessionId, locationId, serviceIds, startDate = null) => {
  try {
    const params = new URLSearchParams({
      session: sessionId,
      location: locationId,
    });

    // Add each service as a separate query parameter
    if (Array.isArray(serviceIds)) {
      serviceIds.forEach(serviceId => {
        params.append('services', serviceId);
      });
    } else {
      params.set('services', serviceIds);
    }

    if (startDate) {
      params.set('start_date', startDate);
    }

    const response = await api.get(`/calendar/calendars/${calendarId}/availability?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching availability:', error);
    throw error;
  }
};

export const submitBooking = async (calendarId, bookingData) => {
  try {
    const response = await api.post(
      `/calendar/calendars/${calendarId}/book`,
      bookingData
    );
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Booking submission failed:', error);
    return {
      success: false,
      status: error.response?.status || 400,
      // CIN-6620: pass the rejection body through untouched so the confirm step
      // can render the server's copy verbatim. The vehicle double-booking guard
      // answers 409 with {code: 'VEHICLE_DOUBLE_BOOKING', message: <the block
      // copy, with the shop's phone interpolated>}; the widget must never
      // reword it and cannot override it.
      code: error.response?.data?.code || null,
      data: error.response?.data || null,
      error: error.response?.data?.message || 'Failed to submit booking. Please try again.'
    };
  }
};

export const sendPhoneVerificationCode = async (sessionId) => {
  try {
    const response = await api.post(
      `/calendar/sessions/${sessionId}/send-validation`
    );
    return response.data;
  } catch (error) {
    console.error('Error sending verification code:', error);
    throw error;
  }
};

export const verifyPhoneCode = async (sessionId, code) => {
  try {
    const response = await api.post(
      `/calendar/sessions/${sessionId}/validate`,
      { code }
    );
    return response.data;
  } catch (error) {
    console.error('Error verifying phone code:', error);
    throw error;
  }
};

export const getSessionCustomers = async (sessionId) => {
  try {
    const response = await api.get(`/calendar/sessions/${sessionId}/customers`);
    return response.data;
  } catch (error) {
    console.error('Error fetching session customers:', error);
    // Return empty array if customers not found
    if (error.response?.status === 404) {
      return [];
    }
    throw error;
  }
};

export const cancelBooking = async (sessionId) => {
  try {
    const response = await api.post(`/calendar/sessions/${sessionId}/cancel`);
    return response.data;
  } catch (error) {
    console.error('Error cancelling booking:', error);
    throw error;
  }
};

export const getSchedule = async (sessionId) => {
  try {
    const response = await api.get(`/calendar/sessions/${sessionId}/schedule`);
    // strip the trailing Z from the appointment_naive field to indicate it's in local time
    if (response.data && response.data.appointment_naive) {
      response.data.appointment_naive = response.data.appointment_naive.replace(/Z$/, '');
    }
    return response.data;
  } catch (error) {
    console.error('Error fetching schedule:', error);
    throw error;
  }
};

export const getVehicleYears = async () => {
  try {
    const response = await api.get('/motor/years');
    return response.data;
  } catch (error) {
    console.error('Error fetching vehicle years:', error);
    throw error;
  }
};

export const getVehicleMakesByYear = async (year) => {
  try {
    const response = await api.get(`/motor/makes?year=${year}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching vehicle makes:', error);
    throw error;
  }
};

export const getVehicleModelsByYearAndMake = async (year, makeId) => {
  try {
    const response = await api.get(`/motor/models?year=${year}&make_id=${makeId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching vehicle models:', error);
    throw error;
  }
};